// CurrentEventsSalesOverview.tsx
import React, { useMemo } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  CartesianGrid,
  ReferenceLine,
  Cell,
  LabelList
} from 'recharts';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle } from 'lucide-react';
import { analyzePastEventsPattern, projectEventSales } from '../services/salesProjectionService';

interface Registration {
  completed_at: string;
  tickets_count: number;
}

interface Event {
  title: string;
  totalSold: number;
  totalAvailable: number;
  start_date?: string;
  registrations: Registration[];
  venue?: {
    _id: string;
    name: string;
    capacity: number;
    location: string;
  };
  venueCapacity?: number;
}

interface PastEvent {
  id: string;
  title: string;
  start_date: string;
  totalSold: number;
  registrations: Registration[];
}

interface CurrentEventsSalesOverviewProps {
  events: Event[];
  onEventClick: (eventTitle: string) => void;
  pastEvents?: PastEvent[]; // Optional past events for projections
}

const CurrentEventsSalesOverview: React.FC<CurrentEventsSalesOverviewProps> = ({ 
  events, 
  onEventClick,
  pastEvents = []
}) => {
  const theme = useTheme();
  
  // Calculate baseline pattern from past events
  const baselinePattern = useMemo(() => {
    if (pastEvents.length < 3) return [];
    return analyzePastEventsPattern(pastEvents);
  }, [pastEvents]);
  
  // Calculate days until event for color coding
  const getDaysUntilEvent = (startDate?: string): number => {
    if (!startDate) return 999;
    const eventDate = new Date(startDate);
    const currentDate = new Date();
    const timeDiff = eventDate.getTime() - currentDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };
  
  // Get bar color based on percentage and days until event
  const getBarColor = (percentage: number, daysUntilEvent: number, isSoldOut: boolean) => {
    if (isSoldOut) return theme.palette.success.main;
    
    // If event is imminent (less than 14 days away) and sales are low
    if (daysUntilEvent < 14 && percentage < 50) {
      return theme.palette.error.main;
    }
    
    // Color tiers based on sales percentage
    if (percentage >= 80) return theme.palette.success.main;
    if (percentage >= 50) return theme.palette.primary.main;
    if (percentage >= 30) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  
  // Process events data for the chart
  const chartData = events.map((event: Event) => {
    // Use venue capacity if available, otherwise use totalAvailable
    const capacity = event.venueCapacity || event.totalAvailable;
    const actualPercentage = capacity ? Math.round((event.totalSold / capacity) * 100) : 0;
    const isSoldOut = actualPercentage >= 92;
    const daysUntilEvent = getDaysUntilEvent(event.start_date);
    
    // Calculate projection if we have enough data
    let projectedSales = event.totalSold;
    let projectedPercentage = actualPercentage;
    let hasProjection = false;
    
    if (baselinePattern.length > 0 && event.registrations && event.registrations.length > 0 && event.start_date) {
      try {
        const projection = projectEventSales(
          {
            start_date: event.start_date,
            totalSold: event.totalSold,
            registrations: event.registrations,
            venueCapacity: event.venueCapacity,
            totalAvailable: event.totalAvailable
          },
          baselinePattern
        );
        
        if (projection.confidence !== 'low') {
          projectedSales = projection.projectedFinalSales;
          projectedPercentage = Math.round((projectedSales / capacity) * 100);
          hasProjection = true;
        }
      } catch (error) {
        console.error('Projection error for event:', event.title, error);
      }
    }
    
    // Calculate days sold out in advance (if applicable)
    let soldOutMessage = '';
    if (isSoldOut && daysUntilEvent > 0) {
      soldOutMessage = `SOLD OUT (${daysUntilEvent} days in advance)`;
    } else if (isSoldOut) {
      soldOutMessage = 'SOLD OUT';
    }
    
    return {
      name: event.title,
      actualPercentage,
      projectedPercentage: hasProjection ? projectedPercentage : actualPercentage,
      // For stacking, we need the additional projected amount
      projectedAdditional: hasProjection ? Math.max(0, projectedPercentage - actualPercentage) : 0,
      sold: event.totalSold,
      projectedSales,
      total: capacity,
      daysUntilEvent,
      color: getBarColor(actualPercentage, daysUntilEvent, isSoldOut),
      isSoldOut,
      soldOutMessage,
      hasProjection,
      // Status for additional visual indicators
      status: 
        isSoldOut ? 'soldout' :
        actualPercentage >= 80 ? 'excellent' :
        actualPercentage >= 50 ? 'good' : 
        daysUntilEvent < 14 && actualPercentage < 30 ? 'urgent' : 'attention'
    };
  }).sort((a, b) => b.actualPercentage - a.actualPercentage);
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const statusColors = {
        soldout: theme.palette.success.main,
        excellent: theme.palette.success.main,
        good: theme.palette.primary.main,
        attention: theme.palette.warning.main,
        urgent: theme.palette.error.main
      };
      
      return (
        <Box 
          sx={{ 
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
            maxWidth: 320
          }}
        >
          <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>{label}</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {data.isSoldOut && <CheckCircle size={16} color={statusColors.soldout} style={{ marginRight: 8 }} />}
            {data.status === 'urgent' && <AlertCircle size={16} color={statusColors.urgent} style={{ marginRight: 8 }} />}
            <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
              <strong>Current:</strong> {data.sold} of {data.total} sold ({data.actualPercentage}%)
            </Typography>
          </Box>
          
          {data.hasProjection && !data.isSoldOut && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <TrendingUp size={16} color={theme.palette.info.light} style={{ marginRight: 8 }} />
              <Typography variant="body2" sx={{ color: theme.palette.info.light }}>
                <strong>Projected:</strong> {data.projectedSales} sales ({data.projectedPercentage}%)
              </Typography>
            </Box>
          )}
          
          {data.daysUntilEvent < 999 && (
            <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.5 }}>
              {data.daysUntilEvent} days until event
            </Typography>
          )}
          
          {data.isSoldOut && (
            <Typography variant="body2" sx={{ color: statusColors.soldout, mt: 0.5, fontWeight: 'bold' }}>
              {data.soldOutMessage}
            </Typography>
          )}
          
          {data.status === 'urgent' && !data.isSoldOut && (
            <Typography variant="body2" sx={{ color: statusColors.urgent, mt: 0.5, fontWeight: 'bold' }}>
              Needs immediate attention!
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };
  
  // Function used to customize the label content
  const labelFormatter = (value: number, entry: any) => {
    if (entry.isSoldOut) {
      return 'SOLD OUT';
    }
    return `${value}%`;
  };
  
  // Configuration for label positioning and styling
  const labelConfig = {
    fill: '#fff',
    formatter: labelFormatter,
    style: {
      fontSize: '12px',
      fontWeight: 'bold',
      textShadow: '0px 0px 3px rgba(0,0,0,0.8)'
    },
    // Only show labels for bars that have enough width
    content: (props: any) => {
      const { width, value, x, y, height, isSoldOut } = props;
      if (width < 50) return null;
      
      const displayText = isSoldOut ? 'SOLD OUT' : `${value}%`;
      
      return (
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: '12px',
            fontWeight: 'bold',
            textShadow: '0px 0px 3px rgba(0,0,0,0.8)'
          }}
        >
          {displayText}
        </text>
      );
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
          Sales Overview {baselinePattern.length > 0 && '& Projections'}
        </Typography>
        {baselinePattern.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)', borderRadius: 0.5 }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Current Sales</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, background: 'rgba(99, 102, 241, 0.3)', border: '1px dashed rgba(99, 102, 241, 0.6)', borderRadius: 0.5 }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Projected Sales</Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          height: 400,
          width: '100%',
          backgroundColor: alpha(theme.palette.background.paper, 0.4),
          borderRadius: 2,
          padding: 2,
          boxShadow: theme.shadows[2],
          border: `1px solid ${theme.palette.divider}`,
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(51, 65, 85, 0.6) 100%)'
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 140, left: 150, bottom: 20 }}
          >
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.8} />
                <stop offset="50%" stopColor="#6366f1" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={1} />
              </linearGradient>
              <linearGradient id="projectedGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.4} />
              </linearGradient>
              <linearGradient id="soldOutGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                <stop offset="100%" stopColor="#059669" stopOpacity={1} />
              </linearGradient>
              <linearGradient id="urgentGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#dc2626" stopOpacity={1} />
              </linearGradient>
              <linearGradient id="warningGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#d97706" stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              stroke={theme.palette.text.secondary}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={145}
              stroke={theme.palette.text.secondary}
              tickLine={false}
              axisLine={false}
              tick={({ x, y, payload }) => {
                const data = chartData.find(item => item.name === payload.value);
                const textProps: React.SVGProps<SVGTextElement> = {
                  x,
                  y,
                  textAnchor: 'end',
                  fill: theme.palette.text.primary,
                  fontSize: 12
                };
                
                return (
                  <g>
                    <text {...textProps} dx={-8} dy={3}>{payload.value}</text>
                    {data && data.daysUntilEvent !== undefined && data.daysUntilEvent < 14 && !data.isSoldOut && (
                      <text {...textProps} dx={-8} dy={18} fill={theme.palette.warning.main} fontSize={10}>
                        {data.daysUntilEvent} days left
                      </text>
                    )}
                    {data && data.isSoldOut && (
                      <text {...textProps} dx={-8} dy={18} fill={theme.palette.success.main} fontSize={10} fontWeight="bold">
                        SOLD OUT
                      </text>
                    )}
                  </g>
                );
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Reference lines for typical targets */}
            <ReferenceLine x={30} stroke={theme.palette.warning.light} strokeDasharray="3 3" label={{ 
              value: "Min", 
              position: "insideBottomRight",
              fill: theme.palette.warning.light,
              fontSize: 12
            }} />
            <ReferenceLine x={80} stroke={theme.palette.success.light} strokeDasharray="3 3" label={{ 
              value: "Target", 
              position: "insideBottomRight",
              fill: theme.palette.success.light,
              fontSize: 12
            }} />
            
            {/* Actual sales bar */}
            <Bar
              dataKey="actualPercentage"
              stackId="a"
              radius={[0, 4, 4, 0]}
              label={labelConfig}
              onClick={(data) => {
                if (data && data.name) {
                  onEventClick(data.name);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {chartData.map((entry, index) => {
                let gradientId = 'barGradient';
                if (entry.isSoldOut) {
                  gradientId = 'soldOutGradient';
                } else if (entry.status === 'urgent') {
                  gradientId = 'urgentGradient';
                } else if (entry.status === 'attention') {
                  gradientId = 'warningGradient';
                }
                
                return (
                  <Cell key={`cell-${index}`} fill={`url(#${gradientId})`} />
                );
              })}
            </Bar>
            
            {/* Projected additional sales (stacked on top) */}
            {baselinePattern.length > 0 && (
              <Bar
                dataKey="projectedAdditional"
                stackId="a"
                radius={[0, 4, 4, 0]}
                fill="url(#projectedGradient)"
                stroke="rgba(99, 102, 241, 0.6)"
                strokeWidth={1}
                strokeDasharray="4 4"
                onClick={(data) => {
                  if (data && data.name) {
                    onEventClick(data.name);
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            )}
            
            {/* Labels showing sold/total */}
            <Bar dataKey="actualPercentage" stackId="labelStack" fill="transparent">
              <LabelList 
                dataKey="sold" 
                position="right" 
                formatter={(value: any, entry: any) => {
                  if (entry && entry.payload) {
                    const { sold, projectedSales, total, isSoldOut, hasProjection } = entry.payload;
                    if (isSoldOut) {
                      return `${sold}/${total} ✓`;
                    }
                    if (hasProjection && projectedSales > sold) {
                      return `${sold}→${projectedSales}/${total}`;
                    }
                    return `${sold}/${total}`;
                  }
                  return '';
                }}
                fill={theme.palette.text.secondary}
                style={{ fontSize: '11px', fontWeight: 500 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default CurrentEventsSalesOverview;